import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// FAQ项的接口定义
interface FaqItemType {
  id: number;
  question: string;
  answer: string;
  answerList?: string[];
}

// FAQ数据集合的接口定义
interface FaqData {
  [category: string]: FaqItemType[];
}

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题区块样式
const HeaderSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 类别选项卡容器样式
const CategoriesContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1rem',
  justifyContent: 'center',
  marginBottom: '2rem'
});

// 类别选项卡样式
const CategoryTab = styled('button', {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    active: {
      true: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9
        }
      },
      false: {
        background: 'transparent',
        color: '$textPrimary',
        border: '1px solid $borderColor',
        '&:hover': {
          background: '$backgroundTertiary'
        }
      }
    }
  },
  
  defaultVariants: {
    active: false
  }
});

// 问题列表容器样式
const FaqContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  maxWidth: '900px',
  margin: '0 auto'
});

// 问题项样式
const FaqItem = styled('div', {
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid $borderColor',
  transition: 'box-shadow 0.3s ease',
  
  '&:hover': {
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)'
  }
});

// 问题标题样式
const FaqQuestion = styled('button', {
  width: '100%',
  padding: '1.25rem 1.5rem',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  textAlign: 'left',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  border: 'none',
  background: '$backgroundSecondary',
  
  '&:focus': {
    outline: 'none'
  }
});

// 问题图标样式
const FaqIcon = styled('span', {
  fontSize: '1.25rem',
  transition: 'transform 0.3s ease',
  
  variants: {
    open: {
      true: {
        transform: 'rotate(180deg)'
      }
    }
  }
});

// 答案容器样式
const FaqAnswer = styled('div', {
  padding: '0',
  maxHeight: '0',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  background: '$backgroundPrimary',
  
  variants: {
    open: {
      true: {
        padding: '1.5rem',
        maxHeight: '500px'
      }
    }
  }
});

// 答案文本样式
const AnswerText = styled('p', {
  fontSize: '1rem',
  lineHeight: 1.6,
  color: '$textSecondary',
  margin: 0
});

// 答案列表样式
const AnswerList = styled('ul', {
  paddingLeft: '1.5rem',
  marginTop: '1rem',
  marginBottom: '0'
});

// 答案列表项样式
const AnswerListItem = styled('li', {
  fontSize: '1rem',
  lineHeight: 1.6,
  color: '$textSecondary',
  marginBottom: '0.5rem'
});

// 答案亮点样式已删除 - 当前未使用

// 搜索区域样式
const SearchSection = styled('div', {
  maxWidth: '600px',
  margin: '0 auto 2rem',
  position: 'relative'
});

// 搜索输入框样式
const SearchInput = styled('input', {
  width: '100%',
  padding: '1rem 1.5rem',
  fontSize: '1rem',
  borderRadius: '30px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  outline: 'none',
  
  '&:focus': {
    borderColor: '$accentPrimary',
    boxShadow: '0 0 0 3px rgba(6, 214, 160, 0.2)'
  }
});

// 搜索图标样式
const SearchIcon = styled('span', {
  position: 'absolute',
  right: '1.5rem',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '$textSecondary',
  fontSize: '1.25rem'
});

// 无结果样式
const NoResults = styled('div', {
  textAlign: 'center',
  padding: '3rem',
  background: '$backgroundSecondary',
  borderRadius: '12px'
});

// 无结果文本样式
const NoResultsText = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  marginBottom: '1rem'
});

// 联系支持样式
const ContactSupport = styled('button', {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  background: '$accentPrimary',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
  
  '&:hover': {
    opacity: 0.9
  }
});

/**
 * 云挖矿常见问题组件
 * 展示云挖矿相关的常见问题和答案
 */
const CloudMiningFaq: React.FC = () => {
  const { t } = useTranslation();
  
  // 开启状态
  const [openItems, setOpenItems] = useState<Record<number, boolean>>({});
  // 当前类别
  const [activeCategory, setActiveCategory] = useState('general');
  // 搜索关键词
  const [searchQuery, setSearchQuery] = useState('');
  
  // 常见问题数据
  const faqData: FaqData = {
    general: [
      {
        id: 1,
        question: t('cloudMining.faq.general.whatIs.question', '什么是云挖矿？'),
        answer: t('cloudMining.faq.general.whatIs.answer', '云挖矿是一种通过远程数据中心共享算力进行加密货币挖矿的方式。用户无需购买、设置和维护实体挖矿设备，只需购买或租用云算力合约，即可参与区块链网络的挖矿过程并获得相应收益。')
      },
      {
        id: 2,
        question: t('cloudMining.faq.general.howWorks.question', '云挖矿如何运作？'),
        answer: t('cloudMining.faq.general.howWorks.answer', '云挖矿运作方式如下：我们在全球战略位置建立专业矿场和数据中心，部署高效的挖矿设备。用户通过购买这些设备的算力份额参与挖矿。我们负责设备的日常维护、电力供应和网络连接，确保24/7不间断运行。当区块被成功挖出并获得奖励时，收益会按照用户购买的算力比例进行分配。')
      },
      {
        id: 3,
        question: t('cloudMining.faq.general.advantages.question', '云挖矿相比传统挖矿有哪些优势？'),
        answer: t('cloudMining.faq.general.advantages.answer', ''),
        answerList: [
          t('cloudMining.faq.general.advantages.list1', '无需专业知识：不需要了解复杂的硬件设置和维护'),
          t('cloudMining.faq.general.advantages.list2', '零硬件投资：避免购买昂贵矿机的高额前期成本'),
          t('cloudMining.faq.general.advantages.list3', '节省电费：我们的数据中心位于电费低廉的地区，降低运营成本'),
          t('cloudMining.faq.general.advantages.list4', '无噪音和热量：避免矿机运行产生的噪音和热量困扰'),
          t('cloudMining.faq.general.advantages.list5', '专业维护：由技术团队负责设备维护和优化'),
          t('cloudMining.faq.general.advantages.list6', '灵活投资：提供多种合约和周期选择，满足不同投资需求')
        ]
      },
      {
        id: 4,
        question: t('cloudMining.faq.general.risks.question', '云挖矿有哪些风险？'),
        answer: t('cloudMining.faq.general.risks.answer', '云挖矿主要风险包括：加密货币价格波动风险、挖矿难度增加导致收益降低、服务提供商可靠性（我们保证7x24小时运行和透明收益分配）、合约条款变更（我们承诺合约期内维持稳定条款）。我们建议用户了解市场动态，并根据自身风险承受能力做出投资决策。')
      }
    ],
    contracts: [
      {
        id: 5,
        question: t('cloudMining.faq.contracts.types.question', '提供哪些类型的挖矿合约？'),
        answer: t('cloudMining.faq.contracts.types.answer', '我们提供多种挖矿合约，覆盖主流加密货币：比特币(BTC)挖矿合约使用SHA-256算法；以太坊(ETH)挖矿合约使用Ethash算法；莱特币(LTC)和狗狗币(DOGE)挖矿合约使用Scrypt算法。每种合约都有不同算力和周期选择，从入门级到专业级，合约期限从180天到720天不等。')
      },
      {
        id: 6,
        question: t('cloudMining.faq.contracts.duration.question', '合约期限多长？'),
        answer: t('cloudMining.faq.contracts.duration.answer', '我们提供灵活的合约期限选择，包括：180天（短期合约）、360天（标准合约）和720天（长期合约）。一般而言，合约期限越长，每单位算力的价格越优惠，但需要考虑更长的资金锁定期和市场变化风险。')
      },
      {
        id: 7,
        question: t('cloudMining.faq.contracts.renew.question', '合约到期后能否续约？'),
        answer: t('cloudMining.faq.contracts.renew.answer', '是的，合约到期前，您将收到续约通知。我们提供便捷的续约流程和忠诚客户折扣。续约时可以选择维持原有算力或进行调整。如选择不续约，合约将在到期后自动终止，不会产生额外费用。')
      },
      {
        id: 8,
        question: t('cloudMining.faq.contracts.terminate.question', '能否提前终止合约？'),
        answer: t('cloudMining.faq.contracts.terminate.answer', '根据我们的合约政策，一旦购买云挖矿合约，原则上不支持提前终止，因为我们已为您预留了相应的硬件资源。特殊情况下，您可以联系客户支持团队协商解决方案，但可能会收取一定的提前终止费用。')
      }
    ],
    payment: [
      {
        id: 9,
        question: t('cloudMining.faq.payment.methods.question', '支持哪些支付方式？'),
        answer: t('cloudMining.faq.payment.methods.answer', '我们接受多种支付方式：加密货币支付（BTC、ETH、USDT等主流币种），信用卡/借记卡支付（Visa、Mastercard），电子钱包支付（PayPal、Skrill）以及部分地区的银行转账。不同支付方式的到账时间和手续费可能有所不同。')
      },
      {
        id: 10,
        question: t('cloudMining.faq.payment.fees.question', '有哪些额外费用？'),
        answer: t('cloudMining.faq.payment.fees.answer', '除了合约购买价格外，主要额外费用包括：维护费（覆盖电力、冷却、网络等运营成本，按日计费并从挖矿收益中扣除）和提现费用（根据您选择的加密货币网络和提现金额而定）。所有费用在购买前都会明确披露，确保透明无隐藏收费。')
      },
      {
        id: 11,
        question: t('cloudMining.faq.payment.refund.question', '是否提供退款？'),
        answer: t('cloudMining.faq.payment.refund.answer', '根据我们的退款政策，在购买后24小时内，如果您的合约尚未开始挖矿，可以申请全额退款。一旦合约激活并开始挖矿，由于资源已被分配，我们通常不提供退款。特殊情况请联系客户支持团队评估。')
      }
    ],
    earnings: [
      {
        id: 12,
        question: t('cloudMining.faq.earnings.calculate.question', '如何计算挖矿收益？'),
        answer: t('cloudMining.faq.earnings.calculate.answer', '挖矿收益计算基于多个因素：您购买的算力大小、当前网络难度、挖矿效率、加密货币市场价格以及维护费用。我们提供收益计算器帮助估算潜在回报，但实际收益可能因上述因素波动而变化。建议使用我们的计算器进行定期评估。')
      },
      {
        id: 13,
        question: t('cloudMining.faq.earnings.payout.question', '收益如何支付？'),
        answer: t('cloudMining.faq.earnings.payout.answer', '挖矿收益每24小时结算一次，添加到您的平台账户余额。您可以选择：立即提现到外部钱包、累积到特定金额再提现，或重新投资购买更多算力。最低提现金额和手续费根据不同加密货币而有所不同。')
      },
      {
        id: 14,
        question: t('cloudMining.faq.earnings.roi.question', '投资回报率(ROI)是多少？'),
        answer: t('cloudMining.faq.earnings.roi.answer', '云挖矿的ROI取决于多种变量，包括合约类型、市场价格波动和网络难度变化。我们的合约通常提供年化8-25%的目标收益率，但这不是保证值。历史数据显示，长期合约在市场周期中通常能提供更稳定的回报。使用我们的收益计算器可获得基于当前参数的估算。')
      },
      {
        id: 15,
        question: t('cloudMining.faq.earnings.taxReporting.question', '如何处理税务问题？'),
        answer: t('cloudMining.faq.earnings.taxReporting.answer', '加密货币挖矿收益在许多国家/地区被视为应税收入。我们提供详细的收益报表，包含所有必要信息供您进行税务申报。然而，税法因地区而异，我们建议咨询专业税务顾问，了解您所在地区的具体税务要求和申报义务。')
      }
    ],
    technical: [
      {
        id: 16,
        question: t('cloudMining.faq.technical.hardware.question', '使用什么挖矿硬件？'),
        answer: t('cloudMining.faq.technical.hardware.answer', '我们使用市场领先的高效挖矿设备，包括比特币挖矿的Antminer S19 Pro和MicroBT Whatsminer M30S++，以太坊挖矿的NVIDIA RTX 3080/3090 GPU阵列，以及专为Scrypt算法优化的设备。我们定期升级硬件以保持竞争力，并确保99.9%的设备正常运行时间。')
      },
      {
        id: 17,
        question: t('cloudMining.faq.technical.dataCenter.question', '数据中心位于哪里？'),
        answer: t('cloudMining.faq.technical.dataCenter.answer', '我们的数据中心战略性地分布在全球多个地区，包括冰岛、俄罗斯、哈萨克斯坦和美国德克萨斯州。这些地区被选中是因为其有利的电力成本、自然冷却条件和友好的监管环境。所有设施均配备先进的安全系统、冗余电源和24/7监控。')
      },
      {
        id: 18,
        question: t('cloudMining.faq.technical.pools.question', '使用哪些矿池？'),
        answer: t('cloudMining.faq.technical.pools.answer', '我们与领先的矿池合作，包括F2Pool、Antpool、Binance Pool和Ethermine等。这些矿池提供高可靠性、低拒绝率和优化的收益。我们持续监控矿池性能，并可能根据盈利能力和稳定性调整矿池策略，以最大化客户收益。')
      },
      {
        id: 19,
        question: t('cloudMining.faq.technical.security.question', '如何保障资金和数据安全？'),
        answer: t('cloudMining.faq.technical.security.answer', '我们采用多层次安全措施保护用户资金和数据：钱包安全使用冷热钱包分离和多重签名技术；平台安全采用SSL加密、DDoS防护和定期安全审计；账户安全提供双因素认证(2FA)和异常活动监控。我们的安全团队7x24小时监控系统，及时应对任何潜在威胁。')
      }
    ],
    support: [
      {
        id: 20,
        question: t('cloudMining.faq.support.contact.question', '如何联系客户支持？'),
        answer: t('cloudMining.faq.support.contact.answer', '您可以通过多个渠道联系我们的客户支持团队：在线聊天支持（工作日7x24小时在线）、电子邮件支持（support@BITENGINE.com，通常24小时内回复）以及提交支持工单（用户中心可用）。对于VIP客户，我们还提供专属客户经理服务。')
      },
      {
        id: 21,
        question: t('cloudMining.faq.support.downtime.question', '发生服务中断如何处理？'),
        answer: t('cloudMining.faq.support.downtime.answer', '我们的数据中心设计有高冗余性，服务中断极为罕见。如果发生意外中断，我们的技术团队会立即处理并恢复服务。对于超过4小时的计划外中断，我们将提供相应的合约延期补偿。所有维护活动均提前通知，并安排在影响最小的时段进行。')
      },
      {
        id: 22,
        question: t('cloudMining.faq.support.updates.question', '如何获取平台更新和通知？'),
        answer: t('cloudMining.faq.support.updates.answer', '您可以通过多种方式获取平台更新和重要通知：登录账户后的控制面板公告、订阅我们的电子邮件通讯、关注我们的社交媒体账号（Twitter、Telegram、Facebook）或加入我们的社区Discord服务器。重要通知也会通过您注册时提供的联系方式直接发送。')
      }
    ]
  };
  
  // 类别列表
  const categories = [
    { id: 'general', name: t('cloudMining.faq.categories.general', '基本问题') },
    { id: 'contracts', name: t('cloudMining.faq.categories.contracts', '合约相关') },
    { id: 'payment', name: t('cloudMining.faq.categories.payment', '支付和费用') },
    { id: 'earnings', name: t('cloudMining.faq.categories.earnings', '收益和提现') },
    { id: 'technical', name: t('cloudMining.faq.categories.technical', '技术细节') },
    { id: 'support', name: t('cloudMining.faq.categories.support', '客户支持') }
  ];
  
  // 切换问题开关状态
  const toggleItem = (id: number) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // 切换类别
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setOpenItems({});
    setSearchQuery('');
  };
  
  // 处理搜索
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setActiveCategory('all');
  };
  
  // 获取过滤后的FAQ项
  const getFilteredFaqs = () => {
    if (!searchQuery && activeCategory !== 'all') {
      return faqData[activeCategory as keyof typeof faqData] || [];
    }
    
    // 搜索所有类别
    const allFaqs = Object.values(faqData).flat();
    
    if (!searchQuery) return allFaqs;
    
    const query = searchQuery.toLowerCase();
    return allFaqs.filter(faq => 
      faq.question.toLowerCase().includes(query) || 
      faq.answer.toLowerCase().includes(query) ||
      (faq.answerList?.some(item => item.toLowerCase().includes(query)) ?? false)
    );
  };
  
  const filteredFaqs = getFilteredFaqs();
  
  return (
    <PageContainer>
      <HeaderSection>
        <Title>{t('cloudMining.faq.title', '云挖矿常见问题')}</Title>
        <Subtitle>
          {t('cloudMining.faq.subtitle', '查找有关云挖矿服务的常见问题解答，帮助您更好地了解我们的平台')}
        </Subtitle>
      </HeaderSection>
      
      <SearchSection>
        <SearchInput 
          type="text" 
          placeholder={t('cloudMining.faq.searchPlaceholder', '搜索问题...')}
          value={searchQuery}
          onChange={handleSearch}
          aria-label={t('cloudMining.faq.searchPlaceholder', '搜索问题...')}
        />
        <SearchIcon>🔍</SearchIcon>
      </SearchSection>
      
      <CategoriesContainer>
        <CategoryTab 
          active={activeCategory === 'all'} 
          onClick={() => handleCategoryChange('all')}
          aria-label={t('cloudMining.faq.allCategories', '所有类别')}
        >
          {t('cloudMining.faq.allCategories', '所有类别')}
        </CategoryTab>
        {categories.map(category => (
          <CategoryTab 
            key={category.id} 
            active={activeCategory === category.id} 
            onClick={() => handleCategoryChange(category.id)}
            aria-label={category.name}
          >
            {category.name}
          </CategoryTab>
        ))}
      </CategoriesContainer>
      
      {filteredFaqs.length > 0 ? (
        <FaqContainer>
          {filteredFaqs.map(faq => (
            <FaqItem key={faq.id}>
              <FaqQuestion 
                onClick={() => toggleItem(faq.id)}
                aria-expanded={openItems[faq.id] || false}
                aria-controls={`faq-answer-${faq.id}`}
              >
                {faq.question}
                <FaqIcon open={openItems[faq.id]}>▼</FaqIcon>
              </FaqQuestion>
              <FaqAnswer 
                open={openItems[faq.id]} 
                id={`faq-answer-${faq.id}`}
                role="region"
                aria-labelledby={`faq-question-${faq.id}`}
              >
                {faq.answer && <AnswerText>{faq.answer}</AnswerText>}
                {faq.answerList && (
                  <AnswerList>
                    {faq.answerList.map((item, index) => (
                      <AnswerListItem key={index}>{item}</AnswerListItem>
                    ))}
                  </AnswerList>
                )}
              </FaqAnswer>
            </FaqItem>
          ))}
        </FaqContainer>
      ) : (
        <NoResults>
          <NoResultsText>
            {t('cloudMining.faq.noResults', '没有找到匹配的问题。请尝试其他搜索词或浏览其他类别。')}
          </NoResultsText>
          <ContactSupport>
            {t('cloudMining.faq.contactSupport', '联系客户支持')}
          </ContactSupport>
        </NoResults>
      )}
    </PageContainer>
  );
};

export default CloudMiningFaq; 